import React from 'react';
import { Button, Row, Col, Card, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faUsers, faUserPlus, faEdit } from '@fortawesome/free-solid-svg-icons';
import { GetChannelRes } from '../../../interfaces/team/TeamandRegion.modal';
import { CHANNEL_TYPE, ROLE_PRIORITY, ROLES } from '../../../Data/SD';
import AvatarImage from '../../ui/Avatar/AvatarImage';
import backarrowbtn from '../../../assets/images/backarrowbtn.svg';

interface SelectedChannelDetailsProps {
    selectedChannel: GetChannelRes;
    setSelectedChannel: (channel: GetChannelRes | null) => void;
    handleEditChannel: (e: React.MouseEvent<HTMLButtonElement>, channel: GetChannelRes) => void;
}

const getGroupTypeVariant = (type: string) => {
    const variants = {
        COMPANY: 'primary',
        TEAM: 'success',
        CUSTOM: 'info'
    };
    return variants[type as keyof typeof variants] || 'secondary';
};

const formatGroupType = (type: string) => {
    return type.charAt(0).toUpperCase() + type.slice(1).replace('-', ' ');
};

const SelectedChannelDetails: React.FC<SelectedChannelDetailsProps> = ({
    selectedChannel,
    setSelectedChannel,
    handleEditChannel
}) => {
    // Count roles by priority: each user only in their highest role
    const roleCounts = {
        [ROLES.PROGRAM_MANAGER]: 0,
        [ROLES.SUPERVISOR]: 0,
        [ROLES.SC]: 0,
    };

    selectedChannel.members.forEach(member => {
        let highestRole: string | null = null;
        let highestPriority = 0;
        member.roles.forEach(roleName => {
            const priority = ROLE_PRIORITY[roleName as keyof typeof ROLE_PRIORITY] || 0;
            if (priority > highestPriority) {
                highestPriority = priority;
                highestRole = roleName;
            }
        });
        if (highestRole && roleCounts.hasOwnProperty(highestRole)) {
            roleCounts[highestRole as keyof typeof roleCounts]++;
        }
    });

    return (
        <>
            {/* Group Header */}
            <div className="top border_tittle top_bar_flx">
                <div className="top_bar_tittle">
                    <h2>
                        <button onClick={() => setSelectedChannel(null)}>
                            <img src={backarrowbtn} alt="back arrow" className="icon_sm" />
                        </button>
                        {selectedChannel.channelName}
                    </h2>
                </div>
                <div className="top_right_flx">
                    <div className="my_badge">
                        <Badge bg={getGroupTypeVariant(selectedChannel.channelType)}>
                            {formatGroupType(selectedChannel.channelType)}
                        </Badge>
                    </div>
                </div>
            </div>
            {selectedChannel.channelType === CHANNEL_TYPE.CUSTOM.toString() && (
                <div className="d-flex gap-2">
                    {/* <Button variant="primary">
                        <FontAwesomeIcon icon={faUserPlus} size="sm" className="me-1" />
                        Add Members
                    </Button> */}
                    <Button variant="secondary" onClick={(e) => handleEditChannel(e, selectedChannel)}>
                        <FontAwesomeIcon icon={faEdit} size="sm" className="me-1" />
                        Edit Group
                    </Button>
                </div>
            )}
           

            {/* Group Stats */}
            <div className="mby_24">
                <div className="row">
                    <div className='col-sm-6 col-md-6 col-lg-6 col-xl-3 mb-4 mb-xl-0'>
                        <div className="common_card_file cir_card h-100">
                            <div className='common_card_txt mb-0'>
                                <p>Total Members</p>
                                <h2>{selectedChannel.memberCount}</h2>
                            </div>
                            <div className="common_card_icon">
                                <div className="icon_color_bg red">
                                    <FontAwesomeIcon icon={faUsers} className="text-primary" size="lg" />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='col-sm-6 col-md-6 col-lg-6 col-xl-3 mb-4 mb-xl-0'>
                        <div className="common_card_file cir_card h-100">
                            <div className='common_card_txt mb-0'>
                                <p>Program Managers</p>
                                <h2>{roleCounts[ROLES.PROGRAM_MANAGER]}</h2>
                            </div>
                            <div className="common_card_icon">
                                <div className="icon_color_bg blue">
                                    <FontAwesomeIcon icon={faUsers} className="text-info" size="lg" />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='col-sm-6 col-md-6 col-lg-6 col-xl-3 mb-4 mb-xl-0'>
                        <div className="common_card_file cir_card h-100">
                            <div className='common_card_txt mb-0'>
                                <p>Supervisors</p>
                                <h2>{roleCounts[ROLES.SUPERVISOR]}</h2>
                            </div>
                            <div className="common_card_icon">
                                <div className="icon_color_bg yellow">
                                    <FontAwesomeIcon icon={faUsers} className="text-warning" size="lg" />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='col-sm-6 col-md-6 col-lg-6 col-xl-3 mb-4 mb-xl-0'>
                        <div className="common_card_file cir_card h-100">
                            <div className='common_card_txt mb-0'>
                                <p>Support Coordinators</p>
                                <h2>{roleCounts[ROLES.SC]}</h2>
                            </div>
                            <div className="common_card_icon">
                                <div className="icon_color_bg green">
                                    <FontAwesomeIcon icon={faUsers} className="text-success" size="lg" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Members List */}
            <div>
                <div className='top border_tittle top_bar_flx'>
                    <div className="top_bar_tittle">
                        <h2>Group Members</h2>
                    </div>
                </div>
                <div className='row'>
                    {selectedChannel.members.map((member) => (
                        <div key={member.userId} className="col-sm-6 col-md-6 col-lg-6 col-xl-4 mb-4">
                            <div className="common_card">
                                <div className="group_members_card">
                                    <div className="group_members_header d-flex flex-column gap-1 mb-2">
                                        <div className="group_members_header_icon">
                                            <AvatarImage userId={member.userId} />
                                        </div>
                                        <h5 className='mb-0'>{member.firstName} {member.lastName}</h5>
                                    </div>
                                    <div className="stats d-flex flex-column gap-1">
                                        <p>{member.email}</p>
                                        <p>{member.roles.join(',')}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </>
    );
};

export default SelectedChannelDetails;